import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSystem } from '@ohif/core';
import PageLayout from '../../components/layouts/PageLayout';
import facilityService from '../../services/facilityService';
import authService from '../../auth/authService';
import { hasRole, ROLES } from '../../utils/roleUtils';
import type { Facility } from '../../types/facility.types';
import FacilityDetailsView from './FacilityDetailsView';

const ViewFacilityDetails: React.FC = () => {
  const navigate = useNavigate();
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;
  const [facility, setFacility] = useState<Facility | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentUser = authService.getUser();

    if (!currentUser || !hasRole(currentUser, ROLES.RIS_ADMIN)) {
      navigate('/');
      return;
    }

    const facilityId = authService.getFacilityId();
    if (!facilityId) {
      uiNotificationService?.show({
        title: 'Error',
        message: 'Facility ID not found. Please contact support.',
        type: 'error',
        duration: 5000,
      });
      setLoading(false);
      return;
    }

    const fetchFacility = async () => {
      try {
        setLoading(true);
        const response = await facilityService.getFacilityById(facilityId);

        if (response.success && response.data) {
          setFacility(response.data);
        } else {
          uiNotificationService?.show({
            title: 'Error',
            message: response.error || 'Failed to fetch facility details',
            type: 'error',
            duration: 5000,
          });
        }
      } catch (error) {
        console.error('Error fetching facility:', error);
        uiNotificationService?.show({
          title: 'Error',
          message: 'An unexpected error occurred while fetching facility details',
          type: 'error',
          duration: 5000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFacility();
  }, [navigate, uiNotificationService]);

  const handleUpdateSuccess = (updatedFacility: Facility) => {
    setFacility(updatedFacility);
  };

  if (loading) {
    return (
      <PageLayout title="Facility Details">
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            color: 'var(--text-primary)',
          }}
        >
          Loading facility details...
        </div>
      </PageLayout>
    );
  }

  if (!facility) {
    return (
      <PageLayout title="Facility Details">
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            color: 'var(--text-primary)',
          }}
        >
          No facility data available
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title=""
      hideTitleInHeader
      breadcrumbs={[{ label: 'Home', path: '/admin-dashboard' }, { label: 'Facility Details' }]}
    >
      <FacilityDetailsView
        facilityData={facility}
        onUpdateSuccess={handleUpdateSuccess}
      />
    </PageLayout>
  );
};

export default ViewFacilityDetails;
